/**
 * The only file in this package that touches a browser global.
 *
 * Both functions are pure adaptation — every decision about *when* to offer,
 * answer or give up lives in `webrtc-session.ts`, which never sees an
 * `RTCPeerConnection`. Keeping the DOM surface in one place this small means
 * the part of multiplayer that can be wrong in interesting ways is also the
 * part that runs under `bun test`.
 */

import { iceServersFor, type IceConfig } from "./ice.ts";
import type { DataChannelLike, IceCandidateInit, PeerConnectionLike, SessionDescription, SocketLike } from "./peer-connection.ts";

function adaptChannel(channel: RTCDataChannel): DataChannelLike {
  const adapter: DataChannelLike = {
    send: (frame) => channel.send(frame),
    close: () => channel.close(),
    onopen: null,
    onmessage: null,
    onclose: null,
  };
  channel.onopen = () => adapter.onopen?.();
  channel.onclose = () => adapter.onclose?.();
  channel.onmessage = (event: MessageEvent) => {
    if (typeof event.data === "string") adapter.onmessage?.(event.data);
  };
  return adapter;
}

function describe(description: RTCSessionDescriptionInit): SessionDescription {
  if (description.type !== "offer" && description.type !== "answer") {
    throw new Error(`unexpected session description type: ${description.type}`);
  }
  return { type: description.type, sdp: description.sdp ?? "" };
}

export function browserPeer(ice: IceConfig): PeerConnectionLike {
  const connection = new RTCPeerConnection({ iceServers: [...iceServersFor(ice)] });
  const adapter: PeerConnectionLike = {
    createDataChannel: (label) => adaptChannel(connection.createDataChannel(label)),
    createOffer: async () => describe(await connection.createOffer()),
    createAnswer: async () => describe(await connection.createAnswer()),
    setLocalDescription: (description) => connection.setLocalDescription(description),
    setRemoteDescription: (description) => connection.setRemoteDescription(description),
    addIceCandidate: (candidate) => connection.addIceCandidate(candidate),
    close: () => connection.close(),
    onicecandidate: null,
    ondatachannel: null,
    onstatechange: null,
  };
  connection.onicecandidate = (event: RTCPeerConnectionIceEvent) => {
    const candidate: IceCandidateInit | null = event.candidate
      ? { candidate: event.candidate.candidate, sdpMid: event.candidate.sdpMid, sdpMLineIndex: event.candidate.sdpMLineIndex }
      : null;
    adapter.onicecandidate?.(candidate);
  };
  connection.ondatachannel = (event: RTCDataChannelEvent) => adapter.ondatachannel?.(adaptChannel(event.channel));
  connection.onconnectionstatechange = () => adapter.onstatechange?.(connection.connectionState);
  return adapter;
}

export function browserSocket(url: string): SocketLike {
  const socket = new WebSocket(url);
  const adapter: SocketLike = {
    send: (frame) => socket.send(frame),
    close: () => socket.close(),
    onopen: null,
    onmessage: null,
    onclose: null,
    onerror: null,
  };
  socket.onopen = () => adapter.onopen?.();
  socket.onclose = () => adapter.onclose?.();
  // `navigator.onLine` is the one signal available synchronously from inside
  // a `WebSocket` error handler — good enough to tell "you have no network"
  // (the common offline case, roadmap item 13) from an ordinary relay failure
  // without a round trip. False negatives (reported online, actually not)
  // just fall back to the generic message, same as before this existed.
  socket.onerror = () =>
    adapter.onerror?.(
      typeof navigator !== "undefined" && navigator.onLine === false
        ? "You appear to be offline — multiplayer needs a network connection."
        : "signalling socket error",
    );
  socket.onmessage = (event: MessageEvent) => {
    if (typeof event.data === "string") adapter.onmessage?.(event.data);
  };
  return adapter;
}
